import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Menu, User, LogOut } from 'lucide-react';
import Sidebar from './Sidebar';
import BASE_URL from '../services/api';

const MainLayout = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    setUserName(localStorage.getItem('userName') || 'User');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsSuperAdmin(user.is_superadmin === true);
  }, []);

  const handleLogout = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
    } catch (err) {
      // still logout even if API fails
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('token');
      localStorage.removeItem('userName');
      localStorage.removeItem('username');
      localStorage.removeItem('userRole');
      localStorage.removeItem('portals');
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-40">
          {/* Left: mobile menu + logo + project name */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-800 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <img
              src="Images/tn-logo.png"
              alt="TN Logo"
              className="w-9 h-9 object-contain"
            />
            <div>
              <h1 className="text-base font-bold text-gray-800 leading-tight">
                ADTW Management Portal
              </h1>
              <p className="text-[10px] text-gray-500 leading-tight hidden sm:block">
                Single Sign-On for ADW, Tribal Welfare & TAHDCO Portals
              </p>
            </div>
          </div>

          {/* Right: user chip + logout */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5">
              <div className="w-7 h-7 bg-gradient-to-br from-[#3d82c4] to-[#2d6fad] flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-semibold text-gray-800 leading-tight max-w-[140px] truncate">
                  {userName}
                </p>
                <p className="text-[10px] text-[#3d82c4] font-medium leading-tight">
                  {isSuperAdmin ? 'Super Admin' : 'Portal User'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowLogoutConfirm(true)}
              title="Logout"
              className="p-2 text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Confirm Logout</h2>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-700">
                  Are you sure you want to logout? You will need to sign in again to access the portal.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowLogoutConfirm(false); handleLogout(); }}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
